/* eslint-disable complexity */
import { SkeletonLoader } from '@toggle/design-system';
import { PriceStatus } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePriceDisplay } from '~/react-query-hooks/prices/usePriceDisplay';

import * as S from './InsightsLivePrice.styles';

const EASTERN_TIMEZONE = 'America/New_York';

const getEasternTimezoneAbbr = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIMEZONE,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find(part => part.type === 'timeZoneName');
  return tzPart?.value ?? 'ET';
};

/**
 * EarningsLivePrice Component
 *
 *  Determine which single price to show based on market conditions
 * - Market Open: Shows primary price with "Market Open" label
 * - Pre-market: Shows secondary price with "Pre-Market" tag
 * - After-hours: Shows secondary price with "After-hours" tag
 * - Fallback: Shows primary price (close) with "As of last close" text
 *
 */

export interface InsightsLivePriceProps {
  entity: Entity;
  isHorizontalView?: boolean;
  showPricePercentage?: boolean;
  showDotSeparator?: boolean;
  proportionChange?: number;
  prevCloseDate?: string;
  lastKnownPrice?: number;
  lastKnownDate?: string;
}

export const InsightsLivePrice = ({
  entity,
  isHorizontalView,
  showPricePercentage = false,
  showDotSeparator = false,
  proportionChange,
  prevCloseDate,
  lastKnownPrice,
  lastKnownDate,
}: InsightsLivePriceProps) => {
  const { t } = useTranslation(['analyze']);
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);

  if (isLoading) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ styles: { width: '120px', height: '40px' } }]}
          withWrapper={false}
        />
      </div>
    );
  }

  if (!isValid || !priceData) {
    return null;
  }

  const { price, mappedEntity, priceFormatter } = priceData;

  const getSinglePriceDisplay = () => {
    const marketHours = price.primary.marketHours;

    if (proportionChange !== undefined) {
      const sign = proportionChange >= 0 ? '+' : '';
      const status =
        proportionChange >= 0 ? PriceStatus.Positive : PriceStatus.Negative;

      return (
        <S.ProportionChangeContainer>
          {lastKnownPrice !== undefined && (
            <S.ProportionChangeColumn>
              <S.PriceRow>
                <S.ProportionPrice>
                  {priceFormatter(lastKnownPrice)}
                </S.ProportionPrice>
                <S.ProportionCurrency>
                  {mappedEntity.currency}
                </S.ProportionCurrency>
              </S.PriceRow>
              {lastKnownDate && (
                <S.DateSpan>
                  {t('analyze:analysis.asOfDate', {
                    date: `${formatInTimeZone(
                      new Date(lastKnownDate),
                      EASTERN_TIMEZONE,
                      'MMM d, h:mm aaa'
                    )} ${getEasternTimezoneAbbr(new Date(lastKnownDate))}`,
                  })}
                </S.DateSpan>
              )}
            </S.ProportionChangeColumn>
          )}
          <S.ProportionChangeColumn>
            <S.PercentageChange $status={status}>
              {sign}
              {proportionChange.toFixed(2)}%
            </S.PercentageChange>
            {prevCloseDate && (
              <S.DateSpan>
                {t('analyze:analysis.sinceDateClose', {
                  date: `${formatInTimeZone(
                    new Date(prevCloseDate),
                    EASTERN_TIMEZONE,
                    'MMM d'
                  )}`,
                })}
              </S.DateSpan>
            )}
          </S.ProportionChangeColumn>
        </S.ProportionChangeContainer>
      );
    }

    if (
      marketHours.isPreMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive
    ) {
      return (
        <S.StyledPriceDisplay
          $showPricePercentage={showPricePercentage}
          $isHorizontalView={isHorizontalView}
          $showDotSeparator={showDotSeparator}
          price={priceFormatter(price.secondary.price)}
          currency={mappedEntity.currency}
          priceChange={price.secondary.priceChange}
          outsideHoursText={price.secondary.outsideHours}
          isPrimary={false}
          showMarketIndicator
        />
      );
    }

    if (
      marketHours.isAfterMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive
    ) {
      return (
        <S.StyledPriceDisplay
          $showPricePercentage={showPricePercentage}
          $isHorizontalView={isHorizontalView}
          $showDotSeparator={showDotSeparator}
          price={priceFormatter(price.secondary.price)}
          currency={mappedEntity.currency}
          priceChange={price.secondary.priceChange}
          outsideHoursText={price.secondary.outsideHours}
          isPrimary={false}
          showMarketIndicator
        />
      );
    }

    if (marketHours.isMarketHours && price.primary?.isLive) {
      return (
        <S.StyledPriceDisplay
          $showPricePercentage={showPricePercentage}
          $isHorizontalView={isHorizontalView}
          $showDotSeparator={showDotSeparator}
          price={priceFormatter(price.primary.price ?? 0)}
          currency={mappedEntity.currency}
          priceChange={price.primary.priceChange}
          showMarketIndicator
          liveLabel={t('analyze:analysis.marketOpen')}
        />
      );
    }

    return (
      <S.StyledPriceDisplay
        $showPricePercentage={showPricePercentage}
        $isHorizontalView={isHorizontalView}
        $showDotSeparator={showDotSeparator}
        price={priceFormatter(price.primary.price ?? 0)}
        currency={mappedEntity.currency}
        priceChange={price.primary.priceChange}
        timeNode={
          <S.DateSpan>{t('analyze:analysis.asOfLastClose')}</S.DateSpan>
        }
        isPrimary={false}
        showMarketIndicator
      />
    );
  };

  return <div data-testid="earnings-live-price">{getSinglePriceDisplay()}</div>;
};
